import { DeleteOutline as DeleteOutlineIcon } from "@mui/icons-material";
import { Box, Divider, IconButton, List, ListItem } from "@mui/material";
import {
  Button,
  ConfirmationModal,
  DrawerContent,
  DrawerCustom,
  DrawerNavBar,
  ToggleContent,
  Typography,
} from "@platform/design-system";
import { isEqual } from "lodash/fp";
import { Fragment, useState } from "react";

import { useRfpStrategicCarriers } from "../../hooks/useRfpStrategicCarriers";
import { useUpdateRfpCarriers } from "../../hooks/useUpdateRfpCarriers";
import {
  CarrierContactType,
  CarrierWithContactsByType,
} from "../../types/carrier";
import { SortCarrier } from "../../types/rfpApi";
import { getCarrierWithContactsByType } from "../../utils/carrier";

import { AddContactDialog } from "./addContactDialog";
import { DrawerAutocomplete } from "./drawerAutocomplete";
import { DrawerCarrierContactsListItem } from "./drawerCarrierContactsListItem";

const CONTACTS_BY_TYPE: { type: CarrierContactType; label: string }[] = [
  {
    type: "sales_rep",
    label: "Sales Rep",
  },
  {
    type: "support",
    label: "Support",
  },
  {
    type: "general",
    label: "General",
  },
];

const hasChanges = (
  original: CarrierWithContactsByType[],
  updated: CarrierWithContactsByType[],
): boolean => {
  if (original.length !== updated.length) {
    return true;
  }
  return !isEqual(original, updated);
};

const isCarrierEqual = (
  c1: CarrierWithContactsByType,
  c2: CarrierWithContactsByType,
): boolean =>
  (c1.carrierId != null && c1.carrierId === c2.carrierId) ||
  c1.carrierName === c2.carrierName;

interface Props {
  rfpId: string;
  carriers: CarrierWithContactsByType[];
  onClose: VoidFunction;
  onSuccess?: VoidFunction;
}

const Drawer = ({
  rfpId,
  carriers,
  onClose,
  onSuccess,
}: Props): React.JSX.Element => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [rfpCarriers, setRfpCarriers] =
    useState<CarrierWithContactsByType[]>(carriers);
  const [addContactDialog, setAddContactDialog] = useState<{
    carrier?: CarrierWithContactsByType;
    contactType?: CarrierContactType;
    label: string;
  }>({ label: "" });
  const query = useRfpStrategicCarriers({ rfpId });
  const allCarriers = query.data?.carriers ?? [];

  const updateMutation = useUpdateRfpCarriers({
    rfpId,
    successMessage: "Carriers updated successfully",
    errorMessage: "Failed to update carriers",
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const handleSelectCarrier = (carrier: SortCarrier): void => {
    const carrierWithContacts = getCarrierWithContactsByType({
      carrierId: carrier.id,
      name: carrier.name,
      contacts: [],
    });
    setRfpCarriers((cs) => {
      // Check for duplicate
      const exists = cs.some((c) => isCarrierEqual(c, carrierWithContacts));
      if (exists) {
        return cs;
      }
      return [carrierWithContacts, ...cs];
    });
  };

  const handleDeleteCarrier = (carrier: CarrierWithContactsByType): void => {
    setRfpCarriers((cs) => cs.filter((c) => c !== carrier));
  };

  const handleDeleteContact = (
    carrier: CarrierWithContactsByType,
    contactType: CarrierContactType,
    contact: string,
  ): void => {
    setRfpCarriers((cs) =>
      cs.map((c) => {
        if (c !== carrier) {
          return c;
        }
        return {
          ...c,
          contactsByType: {
            ...c.contactsByType,
            [contactType]: c.contactsByType[contactType].filter(
              (e) => e !== contact,
            ),
          },
        };
      }),
    );
  };

  const closeAddContactDialog = (): void => {
    // keep label on close for visual stability
    setAddContactDialog((old) => ({ label: old.label }));
  };

  const handleSubmitContact = (contact: string): string | void => {
    if (!addContactDialog?.carrier || !addContactDialog.contactType) {
      return;
    }

    const { carrier, contactType } = addContactDialog;
    if (carrier.contactsByType[contactType].includes(contact)) {
      return "This contact already exists.";
    }

    setRfpCarriers((cs) =>
      cs.map((c) => {
        if (c !== carrier) {
          return c;
        }
        return {
          ...c,
          contactsByType: {
            ...c.contactsByType,
            [contactType]: [...c.contactsByType[contactType], contact],
          },
        };
      }),
    );
  };

  const handleSaveCarriers = (): void => {
    if (!hasChanges(carriers, rfpCarriers)) {
      onClose();
      return;
    }

    const payload = {
      carriers: rfpCarriers.map((carrier) => ({
        carrierName: carrier.carrierName,
        contactInfo: Object.entries(carrier.contactsByType).flatMap(
          ([type, contacts]) =>
            contacts.map((email) => ({
              type: type as CarrierContactType,
              email,
            })),
        ),
      })),
    };

    updateMutation.mutate(payload);
  };

  const handleClose = (): void => {
    if (hasChanges(carriers, rfpCarriers)) {
      setHasUnsavedChanges(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <DrawerCustom anchor="right" size="lg" open>
        <DrawerNavBar>
          <Box
            display="flex"
            alignItems="center"
            flexGrow={1}
            columnGap={2}
            py={2}
          >
            <ToggleContent
              showButtonLabel="Add Carrier"
              hideButtonLabel="Cancel"
            >
              {(isExpanded) => (
                <Box flex={1}>
                  {isExpanded ? (
                    <DrawerAutocomplete
                      carriers={allCarriers}
                      onSelectCarrier={handleSelectCarrier}
                    />
                  ) : (
                    <Typography variant="body-large">
                      Edit Carriers To Quote
                    </Typography>
                  )}
                </Box>
              )}
            </ToggleContent>
          </Box>
        </DrawerNavBar>
        <DrawerContent>
          {rfpCarriers.length > 0 ? (
            <List>
              {rfpCarriers.map((carrier) => (
                <Fragment key={carrier.carrierId ?? carrier.carrierName}>
                  <ListItem
                    sx={{
                      alignItems: "flex-start",
                      flexDirection: "column",
                      px: 1,
                      "&:hover": {
                        backgroundColor: "surfaces.neutral",
                      },
                      "&:hover .delete-carrier-button": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box width="100%" display="flex">
                      <Box width="100%" maxWidth="calc(100% - 30px)">
                        <Typography variant="body-large" px={1}>
                          {carrier.carrierName}
                        </Typography>
                        <List>
                          {CONTACTS_BY_TYPE.map(({ type, label }) => (
                            <DrawerCarrierContactsListItem
                              key={type}
                              label={label}
                              contacts={carrier.contactsByType[type]}
                              isSalesRep={type === "sales_rep"}
                              onDeleteContact={(contact) =>
                                handleDeleteContact(carrier, type, contact)
                              }
                              onAddContact={() =>
                                setAddContactDialog({
                                  carrier,
                                  contactType: type,
                                  label,
                                })
                              }
                            />
                          ))}
                        </List>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <IconButton
                          className="delete-carrier-button"
                          onClick={() => handleDeleteCarrier(carrier)}
                          size="small"
                          title="Delete carrier"
                          aria-label="Delete carrier"
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.2s ease-in-out",
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </ListItem>
                  <Divider component="li" />
                </Fragment>
              ))}
            </List>
          ) : (
            <Typography variant="body-large">No carriers added</Typography>
          )}
        </DrawerContent>
        <DrawerNavBar bottom>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            flexGrow={1}
            gap={2}
          >
            <Button
              size="lg"
              variant="tertiary"
              onClick={handleClose}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              data-testid="edit-carriers-drawer-save"
              size="lg"
              variant="primary"
              onClick={handleSaveCarriers}
              loading={updateMutation.isPending}
              disabled={rfpCarriers.length === 0}
            >
              Save
            </Button>
          </Box>
        </DrawerNavBar>
        <AddContactDialog
          open={!!addContactDialog.carrier}
          onClose={closeAddContactDialog}
          onAddContact={handleSubmitContact}
          label={addContactDialog.label}
        />
      </DrawerCustom>

      <ConfirmationModal
        open={hasUnsavedChanges}
        title="Discard changes?"
        description="You have unsaved changes. Are you sure you want to discard them?"
        confirmButtonText="Discard"
        onAccept={onClose}
        cancelButtonText="Cancel"
        onCancel={() => setHasUnsavedChanges(false)}
      />
    </>
  );
};

export { Drawer as EditRfpCarriersDrawer };
